import * as React from "react";
import * as Moment from 'moment';
import { inputClasses } from '../validation/ValidationClasses';
import { FieldState, FieldApi } from "informedv4";
import moment = require("moment");

interface DatePickerProps {
    name: string;
    id: string;
    label: string;
    fieldState: FieldState;
    fieldApi: FieldApi;
    setValue: (value: string) => void;
    className: string;
    disabled?: boolean;
    required: boolean;
    acceptDateRange?: DatePickerAcceptDateRange;
    onBlur?: (value: string) => void;
    includeTime?: boolean;
    placeholder?: string;
}

export enum DatePickerAcceptDateRange {
    All,
    PastOrPresent,
}

const nativeFormat = (withTime?: boolean) => {
    if (!withTime) return "YYYY-MM-DD";
    return "YYYY-MM-DDTHH:mm";
};

const toNative = (v?: string, withTime?: boolean) => {
    if (!v) return "";
    const fmt = nativeFormat(withTime);
    const m = Moment(v);
    if (m.isValid()) return m.format(fmt);
    const strict = Moment(v, fmt, true);
    return strict.isValid() ? strict.format(fmt) : "";
};

export const DatePicker = ({ id, name, label, fieldState, fieldApi, setValue, className, onBlur, disabled, required, acceptDateRange, includeTime, placeholder }: DatePickerProps) => {
    const { value } = fieldState;

    const valueFormatted = toNative(
        value as string,
        includeTime
    );

    return <>
        <div className={className}>
            <label htmlFor={id}>{label}</label>
            <DatePickerNoForm
                id={id}
                name={name}
                value={valueFormatted}
                className={inputClasses(fieldApi.getError() as string)}
                disabled={disabled}
                placeholder={placeholder}
                required={required}
                onChange={setValue}
                onBlur={onBlur}
                includeTime={includeTime}
                acceptDateRangeOptions={acceptDateRange}
            />
            {fieldApi.getError() ? <span className="field-validation-error">{fieldApi.getError() as string}</span> : null}
        </div>
    </>
}

const formatString = (acceptDateTime?: boolean) => {
    if (acceptDateTime)
        return "YYYY-MM-DD HH:mm:ss";

    return "YYYY-MM-DD";
}

const maxDate = (acceptDateRange: DatePickerAcceptDateRange, acceptDateTime?: boolean): string => {
    if (acceptDateRange === DatePickerAcceptDateRange.PastOrPresent)
        return Moment(new Date()).format(formatString(acceptDateTime));

    return Moment(new Date("9999-12-31")).format(formatString(acceptDateTime));
}

interface DatePickerNoFormProps {
    id?: string | undefined;
    value: string;
    className: string;
    onChange: (value: any) => void;
    onBlur?: (value: any) => void | null;
    placeholder: string;
    disabled?: boolean;
    required?: boolean;
    from?: boolean;
    acceptDateRangeOptions?: DatePickerAcceptDateRange,
    includeTime?: boolean;
    name: string;
}
export const DatePickerNoForm = ({ id, name, value, className, onChange, onBlur = null, placeholder, disabled = false, required = false, from = true, acceptDateRangeOptions = DatePickerAcceptDateRange.All, includeTime = false }: DatePickerNoFormProps) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const format = nativeFormat(includeTime);

    React.useEffect(() => {
        if (!inputRef.current) return;
        const m = Moment(value, format, true);
        inputRef.current.value = m.isValid() ? m.format(format) : "";
    }, [value, format]);

    return (
        <input
            ref={inputRef}
            name={name}
            id={id}
            type={includeTime ? "datetime-local" : "date"}
            max={maxDate(acceptDateRangeOptions, includeTime)}
            className={className}
            placeholder={placeholder}
            onChange={() => {
            }}
            onBlur={(e) => {
                const raw = e.target.value;
                const m = Moment(raw, format, true);
                const normalized = m.isValid() ? m.format(format) : "";
                if (inputRef.current) inputRef.current.value = normalized;

                onChange?.(normalized);
                onBlur?.(normalized);
            }}
            disabled={disabled}
            required={required}
            data-automationid={(from ? "from" : "to") + "-date"}
        />
    );
}
